import axios from "axios";

const api = axios.create({
  baseURL: "/v1/api",
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Only set x-environment from localStorage if not already explicitly set per-request
  if (!config.headers["x-environment"]) {
    config.headers["x-environment"] = localStorage.getItem("selected_env") || "dev";
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("jwt_token");
      localStorage.removeItem("jwt_user");
      window.location.href = "/";
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  googleLogin: (code) => api.post("/auth/google", { code }),
  me: () => api.get("/auth/me"),
};

export const tenantApi = {
  getAll: () => api.get("/tenants"),
  getAllForEnv: (env) => api.get("/tenants", { headers: { "x-environment": env } }),
  getCopyTargetsForEnv: (env) => api.get("/tenants/copy-targets", { headers: { "x-environment": env } }),
};

export const configApi = {
  getConfig: (schema) =>
    api.get("/appconfig", { headers: { "x-tenant-schema": schema } }),
  createConfig: (schema, data) =>
    api.post("/appconfig", data, { headers: { "x-tenant-schema": schema } }),
  updateConfig: (schema, id, data) =>
    api.put(`/appconfig/${id}`, data, { headers: { "x-tenant-schema": schema } }),
  copyToEnvs: (data) =>
    api.post("/config-copy", data),
};

export const auditApi = {
  getLogs: (params) => api.get("/audit-logs", { params }),
};

export const envsApi = {
  getAvailable: () => api.get("/envs"),
};

export const userApi = {
  getAll: () => api.get("/users"),
  create: (data) => api.post("/users", data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateAccess: (id, data) => api.put(`/users/${id}/access`, data),
  getGlobalPermissions: () => api.get("/users/global-permissions"),
  updateGlobalPermissions: (id, data) => api.put(`/users/${id}/global-permissions`, data),
};

export const roleApi = {
  getAll: () => api.get("/roles"),
  getPermissions: () => api.get("/roles/permissions"),
  create: (data) => api.post("/roles", data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
};

export const adminApi = {
  getMigrateInfo: () => api.get("/admin/migrate-info"),
  migrateCentral: (data) => api.post("/admin/migrate-central", data),
};

export const sqlConsoleApi = {
  // env rides on the x-environment header via the interceptor (global selector)
  run: (tenant, sql) => api.post("/sql-console", { tenant, sql }),
};

export const aiAssistantApi = {
  // env is explicit — the sq-ai sandbox has its own env selector, separate
  // from the admin panel's global one.
  getTenantTerminology: (env, tenant) =>
    api.get("/ai-assistant/tenant-terminology", { params: { tenant }, headers: { "x-environment": env } }),
  // Context picker: the tenant's askable modules (labels + disabled resolved
  // server-side) and a module's currently-usable templates.
  getContextModules: (env, tenant) =>
    api.get("/ai-assistant/context/modules", { params: { tenant }, headers: { "x-environment": env } }),
  getContextTemplates: (env, tenant, module) =>
    api.get("/ai-assistant/context/templates", { params: { tenant, module }, headers: { "x-environment": env } }),
};

export const analyticsApi = {
  getTenants: () => api.get("/analytics/google/tenants"),
  getOverview: (params) => api.get("/analytics/google/overview", { params }),
  getUsersOverTime: (params) => api.get("/analytics/google/users-over-time", { params }),
  getClientBreakdown: (params) => api.get("/analytics/google/client-breakdown", { params }),
  getDeviceBreakdown: (params) => api.get("/analytics/google/device-breakdown", { params }),
  getClientDeviceBreakdown: (params) => api.get("/analytics/google/client-device-breakdown", { params }),
  getTopModules: (params) => api.get("/analytics/google/top-modules", { params }),
  getModuleGroupBreakdown: (params) => api.get("/analytics/google/module-group-breakdown", { params }),
  getActivityByHour: (params) => api.get("/analytics/google/activity-by-hour", { params }),
  getActivityByHourPerClient: (params) => api.get("/analytics/google/activity-by-hour-per-client", { params }),
  getMonthlyTrend: (params) => api.get("/analytics/google/monthly-trend", { params }),
  getClientModulePivot: (params) => api.get("/analytics/google/client-module-pivot", { params }),
  getEngagementByClient: (params) => api.get("/analytics/google/engagement-by-client", { params }),
};

export const statusPageApi = {
  getStatus: () => api.get("/status-page"),
};

export default api;
